export function cleanPath(path) {
  if (!path) return '';
  return path.replace(/\/+/g, '/').replace(/\/$/, '') || '/';
}

export function ensureLeadingSlash(path) {
  if (!path) return '/';
  return path[0] === '/' ? path : `/${path}`;
}

export function expandOptionalGroups(pattern) {
  const match = pattern.match(/\(([^)]+)\)/);
  if (!match) return [pattern];

  const [group, content] = match;
  const withGroup = pattern.replace(group, content);
  const withoutGroup = pattern.replace(group, '');
  return [
    ...expandOptionalGroups(withGroup),
    ...expandOptionalGroups(withoutGroup),
  ];
}

export function explicitRoutePathVariants(path) {
  if (!path || path === '*') return [path || ''];

  return expandOptionalGroups(path)
    .map(expandedPath => cleanPath(expandedPath))
    .filter((expandedPath, index, paths) => paths.indexOf(expandedPath) === index);
}

export function interpolateRoutePath(path, params = {}) {
  let result = path;

  result = result.replace(/\(([^)]+)\)/g, (match, content) => {
    const keys = Array.from(content.matchAll(/:([A-Za-z0-9_]+)/g)).map(item => item[1]);
    return keys.every(key => params[key] !== undefined && params[key] !== null) ? content : '';
  });

  result = result.replace(/:([A-Za-z0-9_]+)/g, (match, key) => {
    const value = params[key];
    return value === undefined || value === null ? match : encodeURIComponent(value);
  });

  return cleanPath(ensureLeadingSlash(result));
}

export function appendQuery(path, query) {
  if (!query) return path;

  const queryParams = new URLSearchParams();
  Object.keys(query).forEach(key => {
    const value = query[key];
    if (value === undefined || value === null) return;
    queryParams.append(key, value);
  });

  const queryString = queryParams.toString();
  return queryString ? `${path}?${queryString}` : path;
}

export function isPlainPath(path) {
  return typeof path === 'string' && (
    path[0] === '/'
    || path[0] === '#'
    || path.match(/^[a-z]+:/i)
  );
}

function missingRouteFallback(name) {
  if (process.env.NODE_ENV !== 'production') {
    throw new Error(`Unknown named route: ${name}`);
  }

  return name;
}

export function resolveNamedRoutePath(route, namedRoutes = {}) {
  if (isPlainPath(route)) return route;
  if (typeof route !== 'string' && !route?.name) return route;

  const name = typeof route === 'string' ? route : route?.name;
  const routeParams = typeof route === 'string' ? {} : route?.params;
  const routeQuery = typeof route === 'string' ? undefined : route?.query;
  const namedPath = namedRoutes[name];
  if (!namedPath) return missingRouteFallback(name);

  return appendQuery(interpolateRoutePath(namedPath, routeParams), routeQuery);
}

export function resolveRouteTo(to, namedRoutes = {}) {
  if (!to) return '/';

  if (typeof to === 'string') {
    if (isPlainPath(to)) return to;
    return resolveNamedRoutePath(to, namedRoutes);
  }

  if (to.name) {
    return resolveNamedRoutePath(to, namedRoutes);
  }

  const pathname = to.pathname || to.path || '/';
  return appendQuery(pathname, to.query);
}

export function samplePathForRoute(path) {
  return interpolateRoutePath(path, sampleParamsForPath(path));
}

export function sampleParamsForPath(path) {
  const params = {};
  const matches = path.matchAll(/:([A-Za-z0-9_]+)/g);
  Array.from(matches).forEach(match => {
    params[match[1]] = `${match[1]}-sample`;
  });
  return params;
}
